import { RRule } from "rrule";

import type {
  RecurrenceDayGroup,
  RecurrenceFrequency,
  RecurrencePosition,
  RecurrenceValue,
  RecurrenceWeekday,
} from "./DateRecurrencePicker.types";
import {
  RECURRENCE_DAY_GROUPS,
  RECURRENCE_WEEKDAYS,
  RECURRENCE_WORKING_DAYS,
} from "./constants";
import type { RecurrenceGetText, RecurrenceLanguage } from "./recurrenceText";

const FREQUENCIES: Record<number, RecurrenceFrequency> = {
  [RRule.DAILY]: "daily",
  [RRule.WEEKLY]: "weekly",
  [RRule.MONTHLY]: "monthly",
  [RRule.YEARLY]: "yearly",
};

export const EMPTY_RECURRENCE: RecurrenceValue = {
  frequency: "none",
  interval: 1,
  businessDays: false,
  weekdays: [],
  monthMode: "position",
  monthDays: [],
  months: [],
  position: 1,
  positionTarget: "MO",
  endMode: "never",
  until: null,
  count: 10,
};

/**
 * Reads a stored rule, e.g. `FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE` or
 * `FREQ=MONTHLY;INTERVAL=1;BYDAY=+4SA`. Anything unreadable comes back as no
 * recurrence rather than throwing, since the field holds free text.
 */
export function parseRecurrence(rule?: string | null): RecurrenceValue {
  const text = (rule ?? "").trim();

  if (!text) {
    return EMPTY_RECURRENCE;
  }

  let options;

  try {
    options = RRule.parseString(text);
  } catch {
    return EMPTY_RECURRENCE;
  }

  const frequency =
    undefined === options.freq ? "none" : (FREQUENCIES[options.freq] ?? "none");

  if ("none" === frequency) {
    return EMPTY_RECURRENCE;
  }

  const interval = options.interval ?? 1;
  const days = weekdaysOf(options.byweekday);
  const positions = numbersOf(options.bysetpos);
  const monthDays = numbersOf(options.bymonthday);

  // A position is carried either by a BYDAY prefix (+4SA) or, when it applies
  // to a group of days, by BYSETPOS next to the day list.
  const nth = nthOf(options.byweekday) ?? positions[0] ?? null;
  const group = groupOf(days);
  const positioned = null !== nth;

  // Monday to Friday every week is what the Daily tab calls a business day.
  const businessDays =
    "weekly" === frequency && 1 === interval && isWorkingWeek(days);

  const end = endOf(options);

  return {
    ...EMPTY_RECURRENCE,
    ...end,
    frequency: businessDays ? "daily" : frequency,
    interval,
    businessDays,
    weekdays: "weekly" === frequency ? days : EMPTY_RECURRENCE.weekdays,
    // A stored rule decides its own mode. The default below only applies to a
    // record that has no rule yet: reading it into a rule that names months
    // but no day would add a BYDAY the record never had.
    monthMode: positioned ? "position" : "days",
    monthDays,
    months: numbersOf(options.bymonth),
    position: positioned ? asPosition(nth) : EMPTY_RECURRENCE.position,
    positionTarget: positioned
      ? (group ?? days[0] ?? EMPTY_RECURRENCE.positionTarget)
      : EMPTY_RECURRENCE.positionTarget,
  };
}

/**
 * Back to the shape already in the database: no RRULE prefix, no DTSTART, and
 * a positional day written as BYDAY=+4SA.
 */
export function serializeRecurrence(value: RecurrenceValue): string {
  if ("none" === value.frequency) {
    return "";
  }

  // Business days have no daily spelling in iCalendar: every weekday is a
  // weekly rule listing Monday to Friday.
  const business = "daily" === value.frequency && value.businessDays;

  const parts = [
    `FREQ=${business ? "WEEKLY" : value.frequency.toUpperCase()}`,
    `INTERVAL=${business ? 1 : Math.max(1, value.interval || 1)}`,
  ];

  if (business) {
    parts.push(`BYDAY=${RECURRENCE_WORKING_DAYS.join(",")}`);
  }

  // An empty list drops its part rather than the whole rule: FREQ=WEEKLY
  // without BYDAY is a valid every-N-weeks rule, and the database holds some.
  if ("weekly" === value.frequency && value.weekdays.length > 0) {
    parts.push(`BYDAY=${value.weekdays.join(",")}`);
  }

  if ("yearly" === value.frequency && value.months.length > 0) {
    parts.push(`BYMONTH=${[...value.months].sort(byNumber).join(",")}`);
  }

  if ("monthly" === value.frequency || "yearly" === value.frequency) {
    if ("days" === value.monthMode) {
      if (value.monthDays.length > 0) {
        parts.push(
          `BYMONTHDAY=${[...value.monthDays].sort(byNumber).join(",")}`,
        );
      }
    } else {
      parts.push(...positionParts(value));
    }
  }

  parts.push(...endParts(value));

  return parts.join(";");
}

/**
 * The sentence shown on the trigger. rrule only knows English, so the words
 * come from the caller and the names from Intl.
 */
export function describeRecurrence(
  rule: string,
  gettext?: RecurrenceGetText,
  language?: RecurrenceLanguage,
): string | null {
  const text = (rule ?? "").trim();

  if (!text) {
    return null;
  }

  try {
    return new RRule(RRule.parseString(text)).toText(gettext, language);
  } catch {
    return null;
  }
}

function positionParts(value: RecurrenceValue): string[] {
  const group =
    RECURRENCE_DAY_GROUPS[value.positionTarget as RecurrenceDayGroup];

  if (group) {
    return [`BYDAY=${group.join(",")}`, `BYSETPOS=${value.position}`];
  }

  const sign = value.position > 0 ? "+" : "";

  return [`BYDAY=${sign}${value.position}${value.positionTarget}`];
}

function endParts(value: RecurrenceValue): string[] {
  if ("count" === value.endMode) {
    return [`COUNT=${Math.max(1, value.count || 1)}`];
  }

  if ("until" === value.endMode && value.until) {
    return [`UNTIL=${value.until.replace(/-/g, "")}T235959Z`];
  }

  return [];
}

function endOf(options: {
  until?: Date | null;
  count?: number | null;
}): Pick<RecurrenceValue, "endMode" | "until" | "count"> {
  if (options.count) {
    return { endMode: "count", until: null, count: options.count };
  }

  if (options.until) {
    return {
      endMode: "until",
      until: options.until.toISOString().slice(0, 10),
      count: EMPTY_RECURRENCE.count,
    };
  }

  return {
    endMode: "never",
    until: null,
    count: EMPTY_RECURRENCE.count,
  };
}

function isWorkingWeek(days: RecurrenceWeekday[]): boolean {
  return (
    days.length === RECURRENCE_WORKING_DAYS.length &&
    RECURRENCE_WORKING_DAYS.every((day) => days.includes(day))
  );
}

function weekdaysOf(byweekday: unknown): RecurrenceWeekday[] {
  return toArray(byweekday)
    .map((entry) => {
      if ("string" === typeof entry) {
        return entry.replace(/[+-]?\d+/, "").toUpperCase();
      }

      const weekday = entry as { weekday?: number };

      return "number" === typeof weekday.weekday
        ? RECURRENCE_WEEKDAYS[weekday.weekday]
        : RECURRENCE_WEEKDAYS[Number(entry)];
    })
    .filter((day): day is RecurrenceWeekday =>
      RECURRENCE_WEEKDAYS.includes(day as RecurrenceWeekday),
    );
}

function nthOf(byweekday: unknown): number | null {
  for (const entry of toArray(byweekday)) {
    if ("string" === typeof entry) {
      const match = entry.match(/^([+-]?\d+)/);

      if (match) {
        return Number(match[1]);
      }

      continue;
    }

    const nth = (entry as { n?: number | null }).n;

    if ("number" === typeof nth && 0 !== nth) {
      return nth;
    }
  }

  return null;
}

function groupOf(days: RecurrenceWeekday[]): RecurrenceDayGroup | null {
  if (days.length < 2) {
    return null;
  }

  const sorted = [...days].sort().join(",");

  for (const [name, group] of Object.entries(RECURRENCE_DAY_GROUPS)) {
    if ([...group].sort().join(",") === sorted) {
      return name as RecurrenceDayGroup;
    }
  }

  return null;
}

function asPosition(nth: number): RecurrencePosition {
  if (nth < 0) {
    return -1;
  }

  return Math.min(Math.max(nth, 1), 5) as RecurrencePosition;
}

function numbersOf(value: unknown): number[] {
  return toArray(value)
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry));
}

function toArray(value: unknown): unknown[] {
  if (null === value || undefined === value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

function byNumber(a: number, b: number): number {
  return a - b;
}
